import Slider from 'rc-slider/lib/Slider';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { VolumeMedium, VolumeOff } from '@videoblocks/react-icons';

import {
  setPendingCurrentVolume,
  setPendingPastVolume,
} from '../../../Audio/actions/AudioActions';
import {
  selectCurrentVolume,
  selectPastVolume,
} from '../../../Audio/selectors/audioSelectors';
import { Popper, Placements } from '../../../storywind';

interface AudioPlayerVolumeControlProps {
  className?: string;
  sliderClassName?: string;
  iconClassName?: string;
}

const AudioPlayerVolumeControl = ({
  className,
  sliderClassName,
  iconClassName,
}: AudioPlayerVolumeControlProps): JSX.Element => {
  const dispatch = useDispatch();
  const currentVolume = useSelector(selectCurrentVolume);
  const pastVolume = useSelector(selectPastVolume);
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleMute = () => {
    if (currentVolume === 0) {
      dispatch(setPendingCurrentVolume(pastVolume));
    } else {
      dispatch(setPendingPastVolume(currentVolume));
      dispatch(setPendingCurrentVolume(0));
    }
  };

  const updateVolume = (newVolume) => {
    const volume = newVolume / 100;
    dispatch(setPendingCurrentVolume(volume));
  };

  const muteLabel = currentVolume === 0 ? 'Unmute' : 'Mute';

  const getVolumeIcon = () => {
    return currentVolume === 0 ? (
      <VolumeOff className={iconClassName} />
    ) : (
      <VolumeMedium className={iconClassName} />
    );
  };

  return (
    <div
      className={`audio-player-volume items-center grid grid-flow-col gap-x-2 ${className}`}
    >
      <Popper
        placement={Placements.Left}
        popperClasses="px-2 py-1 text-white bg-black rounded opacity-90 text-sm"
        showContent={showTooltip}
        hideContent={() => setShowTooltip(false)}
        hideOnOutsideClick={false}
        triggerElement={(referenceElement) => (
          <button
            type="button"
            aria-label={muteLabel}
            className="flex items-center p-0 bg-transparent border-none cursor-pointer"
            ref={referenceElement}
            onClick={toggleMute}
            onFocus={() => setShowTooltip(true)}
            onMouseEnter={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {getVolumeIcon()}
          </button>
        )}
      >
        <span>{muteLabel}</span>
      </Popper>

      <Slider
        className={`volume-slider flex items-center ${sliderClassName}`}
        min={0}
        max={100}
        step={1}
        defaultValue={100}
        value={currentVolume * 100}
        onChange={(value) => updateVolume(value)}
      />
    </div>
  );
};

export default AudioPlayerVolumeControl;
